import { formatMinor, type JobStatus } from '@hvac/contracts';
import { Alert, Card, CardBody, cn, EmptyState, PageHeader, Pill, Skeleton } from '@hvac/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { jobsQuery } from '../../shared/api/parties-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';

/**
 * Jobs.
 *
 * The unit of profit. Every line of cost allocated to a job flows into the
 * margin shown here, which is the number the whole business runs on, and the
 * reason job costing exists at all.
 */

const STATUS_LABELS: Record<JobStatus, string> = {
  lead: 'Lead',
  estimate: 'Estimate',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
  invoiced: 'Invoiced',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

function margin(invoicedMinor: string, costMinor: string): bigint {
  return BigInt(invoicedMinor) - BigInt(costMinor);
}

export function JobsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const jobs = useQuery(jobsQuery(organizationId));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        eyebrow="Business"
        title="Jobs"
        description="The unit of profit. Costs allocated on records flow into each job's margin, the number the business actually runs on."
      />

      {jobs.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : jobs.isError ? (
        <Alert tone="danger" title="Jobs could not be loaded">
          {jobs.error.message}
        </Alert>
      ) : jobs.data.length === 0 ? (
        <EmptyState
          icon="job"
          title="No jobs yet"
          body="They arrive as Housecall Pro syncs and as records allocate cost to them. The margin column starts meaning something the day both sides exist."
        />
      ) : (
        <Card>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="sticky top-0 z-10 bg-surface text-left text-xs uppercase tracking-wider text-content-subtle shadow-[inset_0_-1px_0_var(--color-border)]">
                    <th className="px-5 py-3 font-semibold">Job</th>
                    <th className="px-5 py-3 font-semibold">Customer</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 text-right font-semibold">Invoiced</th>
                    <th className="px-5 py-3 text-right font-semibold">Cost</th>
                    <th className="px-5 py-3 text-right font-semibold">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.data.map((job) => {
                    const jobMargin = margin(job.invoicedAmountMinor, job.costAmountMinor);
                    return (
                      <tr
                        key={job.id}
                        className="border-b border-border transition-colors duration-[var(--duration-quick)] odd:bg-surface-muted/40 last:border-0 hover:bg-brand-50/60"
                      >
                        <td className="px-5 py-3">
                          <Link
                            to="/jobs/$jobId"
                            params={{ jobId: job.id }}
                            className="font-medium text-content hover:text-brand-700"
                          >
                            {job.jobNumber === null ? job.title : `${job.jobNumber}, ${job.title}`}
                          </Link>
                          {job.isWarranty ? (
                            <Pill tone="warning" className="ml-2">
                              warranty
                            </Pill>
                          ) : null}
                        </td>
                        <td className="px-5 py-3 text-content-muted">{job.customerName ?? '-'}</td>
                        <td className="px-5 py-3">
                          <Pill
                            tone={
                              job.status === 'cancelled'
                                ? 'neutral'
                                : job.status === 'completed' ||
                                    job.status === 'invoiced' ||
                                    job.status === 'closed'
                                  ? 'success'
                                  : job.status === 'in_progress'
                                    ? 'info'
                                    : 'neutral'
                            }
                          >
                            {STATUS_LABELS[job.status]}
                          </Pill>
                        </td>
                        <td className="tabular px-5 py-3 text-right">
                          {formatMinor(job.invoicedAmountMinor)}
                        </td>
                        <td className="tabular px-5 py-3 text-right">
                          {formatMinor(job.costAmountMinor)}
                        </td>
                        <td
                          className={cn(
                            'tabular px-5 py-3 text-right font-medium',
                            jobMargin < 0n ? 'text-danger' : 'text-content',
                          )}
                        >
                          {formatMinor(jobMargin.toString())}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border bg-surface-muted/60 font-medium">
                    <td className="px-5 py-3 text-xs uppercase tracking-wider text-content-subtle">
                      {jobs.data.length} job{jobs.data.length === 1 ? '' : 's'}
                    </td>
                    <td colSpan={2} />
                    <td className="tabular px-5 py-3 text-right">
                      {formatMinor(
                        jobs.data
                          .reduce((sum, job) => sum + BigInt(job.invoicedAmountMinor), 0n)
                          .toString(),
                      )}
                    </td>
                    <td className="tabular px-5 py-3 text-right">
                      {formatMinor(
                        jobs.data
                          .reduce((sum, job) => sum + BigInt(job.costAmountMinor), 0n)
                          .toString(),
                      )}
                    </td>
                    <td className="tabular px-5 py-3 text-right">
                      {formatMinor(
                        jobs.data
                          .reduce(
                            (sum, job) =>
                              sum + margin(job.invoicedAmountMinor, job.costAmountMinor),
                            0n,
                          )
                          .toString(),
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
